import styled from '@emotion/styled';
import {
  type EntityEvent,
  EntityEventLabels,
  EntityEventState,
  EntityEventStateLabels,
  type EntityEventStateWithoutNew,
  EntityEventType,
  EntityType,
} from '@racemap/sdk/schema/entityEvents';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatLabel } from '@racemap/utilities/formatting';
import type { RacemapEventCommon } from '@racemap/utilities/types/events';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { Button, Dropdown, List, Tag, Typography } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { toast } from 'react-toastify';
import { useUser } from 'src/lib/customHooks';
import { useEventStarter } from 'src/lib/customHooks/useEventStarter';
import { useStarter } from 'src/lib/customHooks/useStarter';
import { useURLState } from 'src/lib/customHooks/useURLState';
import { useMonitorActions, useSelectedStarterId } from 'src/store/monitor_beta/monitorStore';
import { RelativeTime, TooltipWithUTCDatetime } from '../BasicComponents';
import { IconRunner, IconUserEdit } from '../Icon';
import { AlertLocationTag } from './AlertLocationTag';
import type { SelectedStarterAlertMenu } from './StarterAlertsMenu';
import { entityEventColorMap } from './utils/entityEventColorMap';
import { isUserAllowedToUpdateAlert } from './utils/isUserAllowedToUpdateAlert';

const { Text } = Typography;

interface Props {
  alert: EntityEvent;
  currentUser: Immutable<User_Legacy> | null;
  event: RacemapEventCommon;
}

export const AlertEntry: FC<Props> = ({ alert, currentUser, event }) => {
  const { starter: eventStarter } = useEventStarter({
    eventId: alert.entityType === EntityType.DEVICES ? event.id : undefined,
    deviceId: alert.entityId.toHexString(),
  });
  const [, setSelectedMenu] = useURLState<SelectedStarterAlertMenu>('selectedStarterAlertMenu');
  const selectedStarterId = useSelectedStarterId();
  const { starter: directStarter } = useStarter(
    alert.entityType === EntityType.STARTERS ? alert.entityId.toHexString() : undefined,
  );
  const starter = eventStarter ?? directStarter;
  const { user } = useUser({
    // load no user if it pseudo user
    // TODO: remove if NO_MOVEMENT and AWAY_FROM_TRACK come from backend
    userId: !alert.stateEditedBy?.equals('5f7b3b3b7f7b7b7b7f7b7b7b')
      ? alert.stateEditedBy
      : undefined,
  });
  const isFrontendGeneratedAlert =
    alert.entityType === EntityType.STARTERS && alert.eventType === EntityEventType.NO_MOVEMENT;
  const { setSelectedStarterId, updateAlertState } = useMonitorActions();

  const setStateAndAssignToSelf = async (state: EntityEventStateWithoutNew) => {
    try {
      await updateAlertState([alert.id], state);
      toast.success('Alert state updated successfully');
    } catch (error) {
      console.error('Failed to update Alert state', error);
      toast.error('Failed to update Alert state');
    }
  };

  const menuItems = Object.values(EntityEventState)
    .filter((state) => state !== EntityEventState.NEW)
    .map((state) => ({
      key: state,
      label: EntityEventStateLabels[state],
    }));

  if (!currentUser) {
    return null;
  }

  return (
    <StyledListItem key={alert.id.toHexString()} alert={alert}>
      <EntryWrapper>
        <ColorBoxOverlay>
          <AlertStateColorBox eventType={alert.eventType} />
        </ColorBoxOverlay>
        <EntryContent>
          <EntryRow>
            <EntryName
              ellipsis
            >{`${EntityEventLabels[alert.eventType]}${isFrontendGeneratedAlert ? ' [GEN]' : ''}`}</EntryName>
            <Time time={alert.occurredAt} />
          </EntryRow>
          <EntryRow>
            <Tag icon={<IconRunner />}>
              {starter ? (
                <Starter
                  onClick={async () => {
                    await setSelectedStarterId(starter.id);
                  }}
                  onDoubleClick={async () => {
                    if (selectedStarterId !== starter.id) await setSelectedStarterId(starter.id);
                    setSelectedMenu('starters');
                  }}
                >
                  {formatLabel([starter.startNumber, starter.name])}
                </Starter>
              ) : (
                <Starter>{alert.entityId.toHexString()}</Starter>
              )}
            </Tag>
            <AlertLocationTag location={alert.location} starterId={starter?.id} />
          </EntryRow>
          <EntryRow>
            {(alert.state === EntityEventState.IN_PROGRESS ||
              alert.state === EntityEventState.RESOLVED ||
              alert.state === EntityEventState.STANDBY) && (
              <InfoAlertState>
                {user && (
                  <Text underline>
                    {currentUser?.id && user?.id && currentUser.id === user.id.toHexString()
                      ? 'You'
                      : `${user.name}`}
                  </Text>
                )}
                <span>updated the alert to</span>
                <Text underline>{EntityEventStateLabels[alert.state]}</Text>
                {alert.stateEditedAt && <RelativeTime time={alert.stateEditedAt} />}
                <span>ago.</span>
              </InfoAlertState>
            )}
            {isUserAllowedToUpdateAlert(alert, currentUser, event) && (
              <AlerStateDropdownWrapper>
                <Dropdown
                  menu={{
                    items: menuItems,
                    onClick: (e) => setStateAndAssignToSelf(e.key as EntityEventStateWithoutNew),
                  }}
                >
                  <Button icon={<IconUserEdit />} size="small" />
                </Dropdown>
              </AlerStateDropdownWrapper>
            )}
          </EntryRow>
        </EntryContent>
      </EntryWrapper>
    </StyledListItem>
  );
};

const AlerStateDropdownWrapper = styled.div`
  margin-left: auto;
  margin-right: 10px;
`;

const InfoAlertState = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time: FC<{ time: Date }> = ({ time }) => {
  return (
    <TooltipWithUTCDatetime datetime={time}>
      <TimeField>
        <RelativeTime time={time} />
      </TimeField>
    </TooltipWithUTCDatetime>
  );
};

const ColorBoxOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
`;

const TimeField = styled.div`
  color: ${RacemapColors.DarkGray};
  padding-left: 10px;
  padding-right: 15px;
  width: 120px;
  display: flex;
  justify-content: flex-end;
`;

const Starter = styled.div`
  display: inline-flex;
  align-items: center;
  padding-left: 8px;
  justify-content: flex-end;
  cursor: pointer;
`;

const EntryWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding-left: 3px;
`;

const EntryContent = styled.div<{ isSelected?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 100%;
`;

const EntryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 5px;
`;

const EntryName = styled(Text)`
  font-size: 16px;
  font-weight: bold;
`;

const StyledListItem = styled(List.Item)<{ alert: EntityEvent }>`
  height: 105px;
  display: flex;
  &:hover {
    background: ${RacemapColors.LightLightGray};
  }
  border-bottom: 1px solid ${RacemapColors.DarkGray};
`;

interface StyledAlertProps {
  eventType: EntityEvent['eventType'];
}

const AlertStateColorBox = styled.div<StyledAlertProps>`
  margin-right: 3px;
  background-color: ${({ eventType }) => entityEventColorMap[eventType] ?? 'white'};
  float: left;
  height: 100px;
  width: 4px;
`;
